// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {
	MACOS_MICROPHONE_PERMISSION_NAME,
	MACOS_PRIVACY_AND_SECURITY_SETTINGS_NAME,
	MACOS_SYSTEM_SETTINGS_NAME,
	PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import {
	CLOSE_DESCRIPTOR,
	OPEN_SETTINGS_DESCRIPTOR,
	UNDERSTOOD_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {isDesktop, isNativeMacOS} from '@app/features/ui/utils/NativeUtils';
import {openNativePermissionSettings} from '@app/features/voice/utils/NativePermissions';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const MICROPHONE_PERMISSION_REQUIRED_DESCRIPTOR = msg({
	message: 'Microphone permission required',
	comment:
		'Title of the modal shown when microphone permission is denied. Keep tone plain (safety/permissions surface).',
});
const NEEDS_ACCESS_TO_YOUR_MICROPHONE_OPEN_ALLOW_AND_DESCRIPTOR = msg({
	message:
		'{productName} needs access to your microphone. Open {macosSystemSettingsName} → {macosPrivacyAndSecuritySettingsName} → {macosMicrophonePermissionName}, allow {productName2}, and then restart the app.',
	comment:
		'Body of the microphone-permission-denied modal on macOS. Walks the user through the System Settings path. Placeholders are macOS UI names; keep OS proper nouns intact.',
});
const NEEDS_ACCESS_TO_YOUR_MICROPHONE_ALLOW_MICROPHONE_ACCESS_DESCRIPTOR = msg({
	message:
		'{productName} needs access to your microphone. Allow microphone access in your operating system privacy settings and restart the app.',
	comment: 'Body of the microphone-permission-denied modal on Windows / Linux. {productName} is Fluxer.',
});
const NEEDS_ACCESS_TO_YOUR_MICROPHONE_TO_ENABLE_VOICE_DESCRIPTOR = msg({
	message:
		'{productName} needs access to your microphone to enable voice chat. Grant microphone permission in your browser settings and try again.',
	comment: 'Body of the microphone-permission-denied modal on web. {productName} is Fluxer.',
});
export const MicrophonePermissionDeniedModal = observer(() => {
	const {i18n} = useLingui();
	if (isDesktop() && isNativeMacOS()) {
		return (
			<ConfirmModal
				title={i18n._(MICROPHONE_PERMISSION_REQUIRED_DESCRIPTOR)}
				description={i18n._(NEEDS_ACCESS_TO_YOUR_MICROPHONE_OPEN_ALLOW_AND_DESCRIPTOR, {
					productName: PRODUCT_NAME,
					macosSystemSettingsName: MACOS_SYSTEM_SETTINGS_NAME,
					macosPrivacyAndSecuritySettingsName: MACOS_PRIVACY_AND_SECURITY_SETTINGS_NAME,
					macosMicrophonePermissionName: MACOS_MICROPHONE_PERMISSION_NAME,
					productName2: PRODUCT_NAME,
				})}
				primaryText={i18n._(OPEN_SETTINGS_DESCRIPTOR)}
				primaryVariant="primary"
				onPrimary={() => openNativePermissionSettings('microphone')}
				secondaryText={i18n._(CLOSE_DESCRIPTOR)}
				data-flx="voice.microphone-permission-denied-modal.confirm-modal"
			/>
		);
	}
	const message = isDesktop()
		? i18n._(NEEDS_ACCESS_TO_YOUR_MICROPHONE_ALLOW_MICROPHONE_ACCESS_DESCRIPTOR, {productName: PRODUCT_NAME})
		: i18n._(NEEDS_ACCESS_TO_YOUR_MICROPHONE_TO_ENABLE_VOICE_DESCRIPTOR, {productName: PRODUCT_NAME});
	return (
		<ConfirmModal
			title={i18n._(MICROPHONE_PERMISSION_REQUIRED_DESCRIPTOR)}
			description={message}
			primaryText={i18n._(UNDERSTOOD_DESCRIPTOR)}
			onPrimary={() => {}}
			secondaryText={false}
			hideCloseButton
			data-flx="voice.microphone-permission-denied-modal.confirm-modal--2"
		/>
	);
});
